/*
  * Copyright Mission 42 Ventures, Inc. All rights reserved.
  * Unauthorized copying, distribution, or use is prohibited.
  */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Progress } from '@backstage/core-components';
import {
  Button,
  Chip,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { Alert, Autocomplete } from '@material-ui/lab';
import RefreshIcon from '@material-ui/icons/Refresh';
import { HowItWorks, HowItWorksStep } from '../common/HowItWorks';
import { AuthorizationBoard } from './AuthorizationBoard';
import { AuthorizationDetailDrawer } from './AuthorizationDetailDrawer';
import {
  AuthzBoard,
  BoardColumn,
  COLUMNS,
  ENV_FILTER_LABEL,
  ENV_FILTER_ORDER,
  EnvFilter,
  columnForFilter,
} from './types';
import { useAuthorizationPolicyApi } from './useAuthorizationPolicyApi';

const HOW_IT_WORKS_KEY = 'm42.authz.howItWorks';

const STEPS: HowItWorksStep[] = [
  {
    title: 'Read the board',
    body: (
      <>
        Columns are rollout position, not health. The tabs pick what a card is
        ranked by: a single env classifies each service by that env alone,
        "All environments" keeps the combined view, where a card sits in the
        column of its <i>least</i> advanced environment because the laggard is
        the actionable end. Click a card for per-env route coverage.
      </>
    ),
  },
  {
    title: 'Check coverage before Phase 2',
    body: (
      <>
        Adding any route removes the proxy's synthesized default route, so
        anything served on the port and missing from the coverage table is
        rejected. The inventory is derived from <code>apiExposed</code> so it
        cannot drift from what the service declares.
      </>
    ),
  },
  {
    title: 'Change it in the repo',
    body: (
      <>
        This view is read-only. The block lives in the service repo under{' '}
        <code>linkerd.authorizationPolicy</code> in{' '}
        <code>gitops/argocd/env/&lt;env&gt;/values.yaml</code>, with the chart
        pinned in <code>gitops/terraform/env/&lt;env&gt;/main.tf</code>.
      </>
    ),
  },
];

const useStyles = makeStyles(theme => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
  },
  legend: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    marginLeft: 'auto',
    flexWrap: 'wrap',
  },
}));

/**
 * Mesh authorization (Linkerd L7) readiness board. Sibling of the network
 * policy graph: same repos, same gitops files, different layer. NetworkPolicy
 * can only scope unmeshed peers, because a meshed caller dials the destination
 * proxy's 4143 rather than the app port, so per-route control is L7 and lives
 * here (PLAT-1088 / PLAT-1261).
 */
export const AuthorizationPolicyBody = () => {
  const classes = useStyles();
  const api = useAuthorizationPolicyApi();
  const [board, setBoard] = useState<AuthzBoard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [teamFilter, setTeamFilter] = useState<string | null>(null);
  // Defaults to platform-ci rather than "All": the sweep is env-major and ci
  // leads it, so a fresh visit lands on the tab with the most going on
  // instead of the worst-env aggregate that motivated this selector
  // (PLAT-1261 follow-up).
  const [envFilter, setEnvFilter] = useState<EnvFilter>('platform-ci');
  const [selected, setSelected] = useState<string | null>(null);

  const load = useCallback(
    async (refresh: boolean) => {
      setLoading(true);
      setError(null);
      try {
        setBoard(await api.getBoard(refresh));
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setLoading(false);
      }
    },
    [api],
  );

  useEffect(() => {
    load(false);
  }, [load]);

  const teams = useMemo(
    () =>
      [
        ...new Set((board?.services ?? []).map(s => s.team).filter(Boolean)),
      ] as string[],
    [board],
  );

  // Per-env-filter classification for every service in scope, computed once
  // and shared by the counts below and the "nothing eligible yet" banner.
  // Services with no row for the selected env drop out, same as the board.
  const columns = useMemo(() => {
    const services = (board?.services ?? []).filter(
      s => !teamFilter || s.team === teamFilter,
    );
    return services
      .map(s => columnForFilter(s, envFilter))
      .filter((c): c is BoardColumn => c !== null);
  }, [board, teamFilter, envFilter]);

  const counts = useMemo(() => {
    const byColumn = Object.fromEntries(
      COLUMNS.map(c => [c, columns.filter(x => x === c).length]),
    );
    const services = (board?.services ?? []).filter(
      s => !teamFilter || s.team === teamFilter,
    );
    // Envs that would fail `helm template` outright, scoped to the selected
    // env (or every env, under "All").
    const renderErrors = services.reduce(
      (n, s) =>
        n +
        s.envs.filter(
          e =>
            (envFilter === 'all' || e.env === envFilter) &&
            e.renderErrors.length > 0,
        ).length,
      0,
    );
    return { byColumn, renderErrors };
  }, [board, teamFilter, envFilter, columns]);

  return (
    <>
      <HowItWorks
        collapseKey={HOW_IT_WORKS_KEY}
        lead={
          <>
            Per-route access control for <b>meshed</b> callers, from{' '}
            <code>linkerd.authorizationPolicy</code> in each service's{' '}
            <code>gitops/argocd</code> env values. NetworkPolicy cannot express
            this: a meshed peer dials the destination proxy's 4143, so L4 port
            scoping only ever constrains unmeshed peers.
          </>
        }
        steps={STEPS}
      />
      <Tabs
        value={envFilter}
        onChange={(_e, v: EnvFilter) => setEnvFilter(v)}
        indicatorColor="primary"
        textColor="primary"
      >
        {ENV_FILTER_ORDER.map(f => (
          <Tab key={f} value={f} label={ENV_FILTER_LABEL[f]} />
        ))}
      </Tabs>
      <div className={classes.toolbar}>
        <Autocomplete
          options={teams}
          value={teamFilter}
          onChange={(_, v) => setTeamFilter(v)}
          style={{ width: 220 }}
          renderInput={params => (
            <TextField {...params} label="Team" size="small" variant="outlined" />
          )}
        />
        <Button
          size="small"
          startIcon={<RefreshIcon />}
          onClick={() => load(true)}
          disabled={loading}
        >
          Refresh
        </Button>
        <Chip size="small" label="read-only" variant="outlined" />
        <div className={classes.legend}>
          <Tooltip title="Environments whose values would fail to render on the pinned chart">
            <span>
              <Chip
                size="small"
                variant="outlined"
                label={`render errors ${counts.renderErrors}`}
                color={counts.renderErrors > 0 ? 'secondary' : 'default'}
              />
            </span>
          </Tooltip>
          {board && (
            <Typography variant="caption" color="textSecondary">
              as of {new Date(board.generatedAt).toLocaleTimeString()}
            </Typography>
          )}
        </div>
      </div>

      {error && <Alert severity="error">{error}</Alert>}
      {!board && !error && <Progress />}
      {/* `every` on an empty array is vacuously true, so an empty board would
          otherwise claim "no service is eligible" when the real problem is
          that nothing was read at all. Those are different problems. */}
      {board && board.services.length === 0 && (
        <Alert severity="warning" style={{ marginBottom: 8 }}>
          No services were read on the last rebuild. Check the catalog and the
          network-policy plugin logs.
        </Alert>
      )}
      {board &&
        board.services.length > 0 &&
        columns.length > 0 &&
        columns.every(c => c === 'notRolledOut') && (
          <Alert severity="info" style={{ marginBottom: 8 }}>
            No service has rolled this out yet
            {envFilter !== 'all' ? ` in ${ENV_FILTER_LABEL[envFilter]}` : ''}.
            The template ships in <code>spring-microservice-0.19.0</code>;
            below that pin, or with no{' '}
            <code>linkerd.authorizationPolicy</code> block at all, nothing
            renders.
          </Alert>
        )}
      {board && (
        <AuthorizationBoard
          board={board}
          teamFilter={teamFilter}
          envFilter={envFilter}
          selected={selected}
          onSelect={setSelected}
        />
      )}
      <AuthorizationDetailDrawer
        service={selected}
        onClose={() => setSelected(null)}
      />
    </>
  );
};
